import React from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  LabelList,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import type { DiretoriaSeriesPoint } from '@/data/diretoriaMock';
import { DiretoriaEmpresasBarChart, type DiretoriaOverviewEmpresaPoint } from '@/components/Diretoria/DiretoriaModalChart';
import { formatCurrency, formatCurrencyCompact } from '@/lib/formatters';

export interface DiretoriaOverviewSectionProps {
  evolucao: DiretoriaSeriesPoint[];
  aging: DiretoriaSeriesPoint[];
  empresas: DiretoriaOverviewEmpresaPoint[];
  agingSubtitle?: string;
  empresasSubtitle?: string;
}

export const DiretoriaOverviewSection: React.FC<DiretoriaOverviewSectionProps> = ({
  evolucao,
  aging,
  empresas,
  agingSubtitle,
  empresasSubtitle,
}) => {
  return (
    <section className="space-y-3" aria-labelledby="diretoria-overview-heading">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
        <h2 id="diretoria-overview-heading" className="font-heading text-lg font-semibold text-foreground">
          Visão geral
        </h2>
        
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="border-border/80 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Evolução — receita vs projetado</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-[300px] w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={evolucao} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="diretoriaFillA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(222 47% 18%)" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="hsl(222 47% 18%)" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="diretoriaFillB" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ca8a04" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#ca8a04" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={40} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="valor" name="Receita (índice)" stroke="hsl(222 47% 18%)" fill="url(#diretoriaFillA)" strokeWidth={2} />
                  <Area type="monotone" dataKey="valor2" name="Projetado" stroke="#ca8a04" fill="url(#diretoriaFillB)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Títulos a receber vencidos por faixa</CardTitle>
            {agingSubtitle ? <p className="text-xs text-muted-foreground">{agingSubtitle}</p> : null}
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-[280px] w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={aging} margin={{ top: 28, right: 12, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    width={44}
                    tickFormatter={(v) => formatCurrencyCompact(Number(v))}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const row = payload[0].payload as DiretoriaSeriesPoint;
                      const q = row.valor2 != null ? Number(row.valor2) : null;
                      return (
                        <div className="rounded-md border border-border bg-background px-2 py-1.5 text-xs shadow-md">
                          <div className="font-medium text-foreground">{row.name}</div>
                          <div className="text-muted-foreground">Saldo: {formatCurrency(Number(row.valor))}</div>
                          {q != null && !Number.isNaN(q) ? (
                            <div className="text-muted-foreground">Títulos: {q}</div>
                          ) : null}
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="valor" fill="#dc2626" name="Saldo (R$)" radius={[4, 4, 0, 0]}>
                    <LabelList
                      dataKey="valor"
                      position="top"
                      content={({ x, y, width, value }) => {
                        const val = Number(value);
                        if (Number.isNaN(val)) return null;
                        const cx = Number(x) + Number(width) / 2;
                        const cy = Number(y) - 6;
                        return (
                          <text
                            x={cx}
                            y={cy}
                            textAnchor="middle"
                            fill="#991b1b"
                            fontSize={10}
                            fontWeight={600}
                          >
                            {formatCurrency(val)}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Valor por empresa (R$)</CardTitle>
            {empresasSubtitle ? <p className="text-xs text-muted-foreground">{empresasSubtitle}</p> : null}
          </CardHeader>
          <CardContent className="pt-0">
            <DiretoriaEmpresasBarChart data={empresas} height={280} />
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
